(() => {
  const GLOBAL_KEY = "GuerayY03MobileRailV1";

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].scan(document);
    return;
  }

  class GuerayMobileRail {
    constructor(root) {
      if (!root || root.dataset.gyMobileRailReady === "true") return;

      this.root = root;
      this.track = root.querySelector("[data-gy-mobile-track]");
      this.cards = this.track
        ? Array.from(this.track.querySelectorAll("[data-gy-mobile-card]"))
        : [];
      this.previousButton = root.querySelector("[data-gy-mobile-prev]");
      this.nextButton = root.querySelector("[data-gy-mobile-next]");
      this.segments = Array.from(
        root.querySelectorAll("[data-gy-mobile-segment]")
      );
      this.mobileQuery = window.matchMedia("(max-width: 760px)");
      this.activeIndex = 0;
      this.frame = 0;
      this.interactionTimer = 0;
      this.dragging = false;
      this.dragMoved = false;
      this.startX = 0;
      this.startScrollLeft = 0;

      if (!this.track || this.cards.length < 2) return;

      root.dataset.gyMobileRailReady = "true";
      this.bind();
      this.update(true);
    }

    bind() {
      this.onScroll = this.onScroll.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerEnd = this.onPointerEnd.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onResize = this.onResize.bind(this);

      this.track.addEventListener("scroll", this.onScroll, { passive: true });
      this.track.addEventListener("wheel", this.onWheel, { passive: false });
      this.track.addEventListener("pointerdown", this.onPointerDown);
      this.track.addEventListener("pointermove", this.onPointerMove);
      this.track.addEventListener("pointerup", this.onPointerEnd);
      this.track.addEventListener("pointercancel", this.onPointerEnd);
      this.track.addEventListener("lostpointercapture", this.onPointerEnd);
      this.track.addEventListener("click", this.onClickCapture, true);

      this.previousButton?.addEventListener("click", () => {
        this.markInteracting();
        this.goTo(this.activeIndex - 1);
      });

      this.nextButton?.addEventListener("click", () => {
        this.markInteracting();
        this.goTo(this.activeIndex + 1);
      });

      this.segments.forEach((segment, index) => {
        segment.addEventListener("click", () => {
          this.markInteracting();
          this.goTo(index);
        });
      });

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener("resize", this.onResize, { passive: true });
      }

      window.addEventListener("orientationchange", this.onResize, {
        passive: true,
      });
    }

    isMobile() {
      return this.mobileQuery.matches;
    }

    markInteracting() {
      this.root.classList.add("is-interacting");
      window.clearTimeout(this.interactionTimer);
      this.interactionTimer = window.setTimeout(() => {
        this.root.classList.remove("is-interacting");
      }, 420);
    }

    getCardLeft(index) {
      const card = this.cards[index];
      if (!card) return 0;

      const trackRect = this.track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      return this.track.scrollLeft + cardRect.left - trackRect.left;
    }

    goTo(index, instant = false) {
      const safeIndex = Math.max(0, Math.min(index, this.cards.length - 1));
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      this.track.scrollTo({
        left: this.getCardLeft(safeIndex),
        behavior: instant || reduceMotion ? "auto" : "smooth",
      });
      this.activeIndex = safeIndex;
      this.paint();
    }

    nearestIndex() {
      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const cardCenter = rect.left + rect.width / 2;
        const nextDistance = Math.abs(cardCenter - trackCenter);
        if (nextDistance < distance) {
          distance = nextDistance;
          nearest = index;
        }
      });

      return nearest;
    }

    paint() {
      this.segments.forEach((segment, index) => {
        const active = index === this.activeIndex;
        segment.classList.toggle("is-active", active);
        segment.setAttribute("aria-current", active ? "true" : "false");
      });

      if (this.previousButton) {
        this.previousButton.disabled = this.activeIndex === 0;
      }
      if (this.nextButton) {
        this.nextButton.disabled = this.activeIndex === this.cards.length - 1;
      }
    }

    update(force = false) {
      if (!this.isMobile()) {
        this.activeIndex = 0;
        this.paint();
        return;
      }

      const nextIndex = this.nearestIndex();
      if (force || nextIndex !== this.activeIndex) {
        this.activeIndex = nextIndex;
        this.paint();
      }
    }

    onScroll() {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.update());
    }

    onWheel(event) {
      if (!this.isMobile() || this.track.scrollWidth <= this.track.clientWidth) {
        return;
      }

      const dominantDelta =
        Math.abs(event.deltaY) > Math.abs(event.deltaX)
          ? event.deltaY
          : event.deltaX;
      if (!dominantDelta) return;

      event.preventDefault();
      this.markInteracting();
      this.track.scrollLeft += dominantDelta;
    }

    onPointerDown(event) {
      if (
        !this.isMobile() ||
        event.pointerType !== "mouse" ||
        event.button !== 0
      ) {
        return;
      }

      this.dragging = true;
      this.dragMoved = false;
      this.startX = event.clientX;
      this.startScrollLeft = this.track.scrollLeft;
      this.track.classList.add("is-dragging");
      this.track.style.scrollSnapType = "none";
      this.track.setPointerCapture(event.pointerId);
      this.markInteracting();
    }

    onPointerMove(event) {
      if (!this.dragging) return;

      const delta = event.clientX - this.startX;
      if (Math.abs(delta) > 4) this.dragMoved = true;
      this.track.scrollLeft = this.startScrollLeft - delta;
      event.preventDefault();
    }

    onPointerEnd(event) {
      if (!this.dragging) return;

      this.dragging = false;
      this.track.classList.remove("is-dragging");
      this.track.style.removeProperty("scroll-snap-type");
      if (this.track.hasPointerCapture?.(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }

      window.requestAnimationFrame(() => {
        this.activeIndex = this.nearestIndex();
        this.goTo(this.activeIndex);
      });
    }

    onClickCapture(event) {
      if (!this.dragMoved) return;
      event.preventDefault();
      event.stopPropagation();
      this.dragMoved = false;
    }

    onResize() {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => {
        if (this.isMobile()) {
          this.goTo(this.activeIndex, true);
        } else {
          this.track.scrollLeft = 0;
          this.activeIndex = 0;
          this.paint();
        }
      });
    }
  }

  const api = {
    scan(scope = document) {
      scope.querySelectorAll("[data-gy-mobile-rail]").forEach((root) => {
        new GuerayMobileRail(root);
      });
    },
  };

  window[GLOBAL_KEY] = api;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => api.scan(document), {
      once: true,
    });
  } else {
    api.scan(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    api.scan(event.target);
  });
})();
